import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const heroImage =
    "https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/zmynld8b_Imagen%2021-4-26%20a%20las%2021.21.jpeg";

export default function Hero({ onBookTour, onVR }) {
    const { t } = useT();
    return (
        <section
            id="top"
            className="relative min-h-[100svh] w-full overflow-hidden grain"
            data-testid="hero-section"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={heroImage}
                    alt="Wynwood Residences — building exterior"
                    className="w-full h-full object-cover object-center scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/55 to-charcoal" />
                <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-charcoal/40 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 pt-24 pb-24 min-h-[100svh] flex flex-col justify-end">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className="max-w-3xl"
                >
                    <div className="flex items-center gap-3 mb-8">
                        <span className="inline-block w-8 h-[1px] bg-gold" />
                        <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                            {t("hero.overline")}
                        </span>
                    </div>

                    <h1 className="font-display text-4xl sm:text-5xl lg:text-[72px] leading-[0.95] tracking-tighter font-bold text-white">
                        {t("hero.headline_before")}
                        <br />
                        <span className="italic font-normal text-gold">
                            {t("hero.headline_highlight")}
                        </span>{" "}
                        {t("hero.headline_after")}
                    </h1>

                    <p className="mt-8 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
                        {t("hero.subhead")}
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={onBookTour}
                            className="group relative inline-flex items-center justify-center gap-2 bg-gold text-charcoal px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold-hover transition-all hover:-translate-y-0.5"
                            data-testid="hero-book-tour-btn"
                        >
                            {t("hero.cta_tour")}
                            <ArrowUpRight
                                size={16}
                                className="transition-transform group-hover:rotate-45"
                            />
                        </button>
                        <button
                            onClick={onVR}
                            className="group inline-flex items-center justify-center gap-2 border border-white/25 text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-white/5 hover:border-gold transition-all"
                            data-testid="hero-vr-btn"
                        >
                            <Play size={14} className="fill-white" />
                            {t("hero.cta_vr")}
                        </button>
                    </div>

                    {/* Micro trust row */}
                    <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-[11px] tracking-[0.2em] uppercase text-white/40">
                        <div>
                            <span className="text-white/70">{t("vp.live.overline")}</span> ·{" "}
                            {t("hero.micro_live")}
                        </div>
                        <div>
                            <span className="text-white/70">{t("vp.work.overline")}</span> ·{" "}
                            {t("hero.micro_work")}
                        </div>
                        <div>
                            <span className="text-white/70">{t("vp.earn.overline")}</span> ·{" "}
                            {t("hero.micro_earn")}
                        </div>
                    </div>
                </motion.div>

                {/* Scroll cue */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    className="absolute bottom-8 right-6 md:right-12 hidden md:flex flex-col items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/40"
                >
                    <span>{t("hero.scroll")}</span>
                    <span className="w-[1px] h-10 bg-gradient-to-b from-gold to-transparent animate-shimmer" />
                </motion.div>
            </div>
        </section>
    );
}
